import { z } from 'zod';

/**
 * Input schema for the member-club guest pass invite flow.
 * Mirrors the `create_guest_pass` RPC signature.
 *
 * Notes:
 *  - `account_id` is the tenant (the venue) account.
 *  - `membership_id` is null for staff-issued / comp / marketing passes.
 *  - `valid_for_date` must be today or later (string-formatted date).
 *  - `apply_credit` is optional store credit (§7a) drawn from the inviter's
 *    `tenant_customers.wallet_balance`. The DB enforces it's <= balance.
 */
/**
 * NOTE: At least one of guest_phone or guest_email is required. We assert
 * that in the server action body rather than via .refine() so the schema
 * stays a plain ZodObject — enhanceAction infers `data` correctly only when
 * the schema is a ZodObject (refine wraps it in ZodEffects which collapses
 * to `unknown` at the action body).
 */
export const CreateGuestPassSchema = z.object({
  account_id: z.string().uuid('Invalid tenant account id'),
  membership_id: z.string().uuid('Invalid membership id').nullable(),
  guest_name: z
    .string()
    .min(1, 'Guest name is required')
    .max(255, 'Guest name is too long'),
  guest_phone: z
    .string()
    .min(4, 'Phone is required')
    .max(20, 'Phone is too long')
    .nullable(),
  guest_email: z
    .string()
    .email('Invalid email')
    .max(320, 'Email too long')
    .nullable(),
  valid_for_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
  age_bucket: z.enum(['adult', 'kid']).default('adult'),
  payer: z.enum(['inviter', 'guest', 'free']).default('inviter'),
  price: z
    .number()
    .nonnegative('Price must be zero or positive')
    .default(0),
  visit_time: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, 'Time must be HH:MM[:SS]')
    .nullable()
    .optional(),
  guest_id_number: z.string().min(1).max(64).nullable().optional(),
  guest_nationality: z.string().min(1).max(80).nullable().optional(),
  inviter_label: z.string().min(1).max(255).nullable().optional(),
  apply_credit: z.number().nonnegative().default(0),
});

export type CreateGuestPassInput = z.infer<typeof CreateGuestPassSchema>;
